import React, { useEffect, useMemo, useState } from 'react'
import { fetchReviewAggregates, type ReviewAggregate } from '../../api/reviews'

type Props = {
  subjectId?: number | string | null
  fallbackCount?: number
}

export default function ReviewSummary({ subjectId, fallbackCount = 0 }: Props) {
  const [agg, setAgg] = useState<ReviewAggregate | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!subjectId) return
      setLoading(true)
      try {
        const data = await fetchReviewAggregates(Number(subjectId))
        if (!cancelled) setAgg(data)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [subjectId])

  const count = useMemo(() => agg?.count_total ?? fallbackCount ?? 0, [agg, fallbackCount])
  const avg = useMemo(() => {
    const v = agg?.bayesian_avg ?? agg?.mean_raw
    return typeof v === 'number' && isFinite(v) ? Math.round(v * 10) / 10 : null
  }, [agg])

  return (
    <div className="mb-2 flex items-center gap-3">
      <div className="flex items-center gap-2">
        {loading ? (
          <div className="h-5 w-24 rounded bg-white/10" />
        ) : avg != null ? (
          <div className="flex items-center gap-1 text-sm">
            <span className="font-semibold">{avg.toFixed(1)}</span>
            <Stars value={avg} />
          </div>
        ) : (
          <div className="text-sm text-white/80">No ratings yet</div>
        )}
      </div>
      <div className="ml-2 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-xs">{count}</div>
    </div>
  )
}

function Stars({ value }: { value: number }) {
  const full = Math.floor(value)
  const half = value - full >= 0.5
  const empty = 5 - full - (half ? 1 : 0)
  const els: React.ReactNode[] = []
  for (let i = 0; i < full; i++) els.push(<i key={`f${i}`} className="fa-solid fa-star text-yellow-400" />)
  if (half) els.push(<i key="h" className="fa-solid fa-star-half-stroke text-yellow-400" />)
  for (let i = 0; i < empty; i++) els.push(<i key={`e${i}`} className="fa-regular fa-star text-yellow-400" />)
  return <div className="flex items-center gap-0.5">{els}</div>
}
